import { useState } from 'react';
import type { ReactNode } from 'react';
import { Filter, Search, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Input } from './Input';
import { Button } from './Button';

export interface FilterBarSearchProps {
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  /** HTML id for E2E tests (e.g. `admin.devices.search`). */
  id?: string;
}

export interface FilterBarProps {
  search?: FilterBarSearchProps;
  /** Controls rendered in the primary (always-visible) row. */
  primary?: ReactNode;
  /** Controls rendered inside the collapsible panel. */
  advanced?: ReactNode;
  /** Active filter chips rendered below the controls. */
  chips?: FilterChip[];
  /** Fired when the user clicks the "clear all" button. */
  onClearAll?: () => void;
  className?: string;
  defaultOpen?: boolean;
}

export interface FilterChip {
  id: string;
  label: ReactNode;
  onRemove?: () => void;
}

/**
 * Toolbar combining a full-width search, an expandable advanced filter panel
 * and a chips row that summarises the active filters. The heavy mobile-
 * friendly styling mirrors TailAdmin's invoices filters.
 */
export function FilterBar({
  search,
  primary,
  advanced,
  chips,
  onClearAll,
  className,
  defaultOpen = false,
}: FilterBarProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      className={cn(
        'rounded-2xl border border-gray-200 bg-white p-3 shadow-card dark:border-white/10 dark:bg-gray-900 dark:shadow-card-dark',
        className,
      )}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
        {search && (
          <div className="min-w-0 flex-1">
            <Input
              id={search.id}
              type="search"
              leftIcon={<Search className="h-4 w-4" aria-hidden="true" />}
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              placeholder={search.placeholder ?? 'Search...'}
            />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto sm:shrink-0">
          {primary}
          {advanced && (
            <Button
              variant="outline"
              size="md"
              leftIcon={<Filter className="h-4 w-4" aria-hidden="true" />}
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
            >
              {open ? 'Hide filters' : 'Filters'}
            </Button>
          )}
        </div>
      </div>
      {advanced && open && (
        <div className="mt-3 w-full min-w-0 rounded-xl border border-gray-200/80 bg-gray-50/90 p-4 sm:p-5 dark:border-white/10 dark:bg-gray-800/60">
          {advanced}
        </div>
      )}
      {chips && chips.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {chips.map((chip) => (
            <span
              key={chip.id}
              className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 dark:bg-brand-900/40 dark:text-brand-200"
            >
              {chip.label}
              {chip.onRemove && (
                <button
                  type="button"
                  onClick={chip.onRemove}
                  className="rounded-full p-0.5 hover:bg-brand-100 dark:hover:bg-brand-800"
                  aria-label="Remove filter"
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              )}
            </span>
          ))}
          {onClearAll && (
            <button
              type="button"
              onClick={onClearAll}
              className="text-xs font-medium text-gray-500 underline-offset-2 hover:text-gray-700 hover:underline dark:text-gray-400 dark:hover:text-gray-200"
            >
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
}
